import { mxCell } from 'mxgraph';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import "./GlyphEditor.css";
import { AppContext } from '../App';
import { GlobalState, Glyph, GlyphChange, GlyphState, IdeaBoxEntry, TaskType } from 'konect-model';
import UserControlService, { UserControlEvent } from '../../services/user-control-service';
import { nanoid } from 'nanoid';
import Backlog from './Backlog';
import { DragDropContext, Droppable, Position } from 'react-beautiful-dnd';
import { DropType } from './GlyphGroupingTable';
import { GlyphEvent, GlyphEventService } from '../../services/event-service';
import { MDBTooltip } from 'mdb-react-ui-kit';
import { cloneDeep } from 'lodash';
import GlyphEditorState from './GlyphEditorState';
import { InfoWindowState } from './InfoWindow';
import { Settings } from '../Settings';
import { MXGraphService } from '../../services/mxgraph-service';
var mx = require("mxgraph")({
    mxImageBasePath: "mxgraph/javascript/src/images",
    mxBasePath: "mxgraph/javascript/src"
})

interface GlyphEditorProps {
    initialGlyph: Glyph;
    compositionMode?: boolean;
    presentationMode?: boolean;
}

export const EDITOR_URL = window.location.origin + "/www/index.html";
export const DEFAULT_XML = '<mxGraphModel><root><mxCell id="0"/><mxCell id="1" parent="0"/></root></mxGraphModel>';

const callbackID = { undoId: nanoid(), redoId: nanoid(), saveId: nanoid() };

const renderEntries : GlyphState[] = [];

export default function GlyphEditor(props: GlyphEditorProps) {

    const { project, setShowInfo } = useContext(AppContext);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [glyph, setGlyph] = useState<Glyph>(props.initialGlyph || new Glyph());
    const [isDragging, setIsDragging] = useState<boolean | string>(false);
    const iframe = useRef<any>();
    const svg = useRef<any>();
    const editor = useRef<any>();
    const graph = useRef<any>();
    const animationAnchor = useRef<Position>({ x: 0, y: 0 });
    const glyphRef = useRef<Glyph>(props.initialGlyph || glyph);
    const [glyphState, setGlyphState] = useState<GlyphState>(glyphRef.current.getDefaultState());
    const [activeGlyphStates, setActiveGlyphStates] = useState<GlyphState[]>([]);
    const activeGlyphStatesRef = useRef<GlyphState[]>([]);
    const glyphStateRef = useRef<GlyphState>(glyphState);
    const [globalState, setGlobalState] = useState<GlobalState>(project.globalStates && project.globalStates.length > 0 ? project.globalStates[0] : new GlobalState(nanoid(), nanoid()));
    const annotationModeRef = useRef(props.presentationMode ? true : false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [exportProgress, setExportProgress] = useState<string>('');

    useEffect(() => {
        UserControlService.disable(true);
        initMessageHandler();

        // once glyph editor is closed reenable/redo
        return () => {
            GlyphEventService.reset();
            UserControlService.enable();
        }
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        GlyphEventService.reset();
        loadEditor(glyph);
        initMessageHandler();

        // THIS RETURN FUNCTION IS CALLED WITH PREVIOUS VALUES BEFORE THE NEW USELAYOUTEFFECT IS EXECUTED, THIS IS USED TO KEEP THE STATE UPON LEAVING THE PAGE
        return () => {
            if (!graph.current || !editor.current) {
                return;
            }

            // Render default state before saving to ensure XML doesn't contain state visualizations
            renderGlyphStates([]);

            // Save to different properties based on mode
            if (props.presentationMode) {
                project.presentationGraphXML = editor.current.createXML(graph.current.model);
            } else {
                project.graphXML = editor.current.createXML(graph.current.model);
            }
            //@ts-ignore
            if (svg.current) glyph.svg = svg.current.children[1].children[0].children[1].cloneNode(true);

        }

    }, [])  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { // disable glyph editor in compose mode, but enable in presentation mode
        if (!graph.current || !editor.current) return;

        // Before entering composition or presentation mode, save current graph with default state
        // This prevents stale state visualizations from persisting in the XML
        if (props.compositionMode || props.presentationMode) {
            // First render default state (use the actual "default" state, not empty array)
            const defState = glyphRef.current?.getDefaultState();
            if (defState) {
                renderGlyphStates([defState]);
            }

            // Then save the XML with default state
            if (props.presentationMode) {
                project.presentationGraphXML = editor.current.createXML(graph.current.model);
            } else {
                project.graphXML = editor.current.createXML(graph.current.model);
            }

            // Now clear all state variables for this mode
            renderEntries.length = 0;
            setActiveGlyphStates([]);
            activeGlyphStatesRef.current = [];
            const defaultGlyphState = glyphRef.current.getDefaultState();
            setGlyphState(defaultGlyphState);
            glyphStateRef.current = defaultGlyphState;
        }

        graph.current.setEnabled(!props.compositionMode || props.presentationMode);
    }, [props.compositionMode, props.presentationMode])

    useEffect(() => {
        activeGlyphStatesRef.current = activeGlyphStates;
    }, [activeGlyphStates])

    // Effect for handling the changes when the user selects a different glyph
    useEffect(() => {
        glyphRef.current = glyph;

        if (!graph.current) {
            return
        }
        glyphStateRef.current = glyphRef.current.getDefaultState();
        setGlyphState(glyphStateRef.current);
        graph.current.setDefaultParent(glyphRef.current.model);
        setActiveGlyphStates([]);
        for (const g of project.glyphs) {
            let cell = g.model;
            if (!cell) continue;
            MXGraphService.disableCell(cell);
        }
        MXGraphService.enableCell(glyphRef.current.model);
        graph.current.orderCells(false, [glyph.model]);
        graph.current.setSelectionCells([]);
        graph.current.isNotInDefaultState = false;

        // Clear renderEntries and render default state when glyph changes
        renderEntries.length = 0;
        activeGlyphStatesRef.current = [];

        refreshGraph();

        // Use requestAnimationFrame to ensure graph is fully rendered before applying default state
        requestAnimationFrame(() => {
            renderGlyphStates([]);
        });
        //graph.current.view.setCurrentRoot(glyph.model);
    }, [glyph])  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setGlyph(props.initialGlyph);
    }, [props.initialGlyph])

    // Ensure default rendering after any global state rendering in composition/presentation mode
    useEffect(() => {
        if ((props.compositionMode || props.presentationMode) && graph.current) {
            // Use setTimeout to ensure this runs after any synchronous rendering
            const timer = setTimeout(() => {
                renderGlyphStates([]);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [props.compositionMode, props.presentationMode, globalState, glyph])

    // listens to the iframe to load the editor
    const initMessageHandler = () => {
        window.onmessage = function (e) {
            e.data === "loaded" && initEditor()
        }
    }

    const initEditor = () => {
        // init editor only once
        if (loaded) { return; }

        loadEditor(glyph);
        setLoaded(true);

        const frame: any = iframe.current;
        const model = graph.current.model;

        // SAVE/REDO/UNDO
        editor.current.onSave = () => {
            UserControlService.save();
        }

        editor.current.undoManager.onUndoAvailable = (notAvailable?: boolean) => {
            !notAvailable && UserControlService.dispatch(UserControlEvent.SAVE_AVAILABLE, true);
            UserControlService.dispatch(UserControlEvent.UNDO_AVAILABLE, !notAvailable);
        }

        editor.current.undoManager.onRedoAvailable = (notAvailable?: boolean) => {
            !notAvailable && UserControlService.dispatch(UserControlEvent.SAVE_AVAILABLE, true);
            UserControlService.dispatch(UserControlEvent.REDO_AVAILABLE, !notAvailable);
        }

        UserControlService.on(UserControlEvent.UNDO, () => {
            editor.current.undoManager.undo();
        }, callbackID.undoId);

        UserControlService.on(UserControlEvent.REDO, () => {
            editor.current.undoManager.redo();
        }, callbackID.redoId);

        UserControlService.on(UserControlEvent.BEFORE_SAVING, () => {
            // Save to different properties based on mode
            if (props.presentationMode) {
                project.presentationGraphXML = editor.current.createXML(graph.current.model);
            } else {
                project.graphXML = editor.current.createXML(graph.current.model);
            }
        }, callbackID.saveId)

        // HANDLE EVENTS FROM OTHER COMPONENT
        GlyphEventService.on(GlyphEvent.REMOVE_LINKS, (entry: IdeaBoxEntry) => {
            entry.linkedCells = [];
            graph.current.setSelectionCells([]);
            graph.current.removeAllLabels();
            graph.current && renderGlyphStates([...activeGlyphStatesRef.current])
        })

        // HANDLE GRAPH EVENTS
        graph.current.addListener(mx.mxEvent.CELLS_REMOVED, (sender: any, evt: any) => {
            removeCellLinks(evt.properties.cells);
            removeCellsFromState(glyphStateRef.current, evt.properties.cells);
            setIsDragging(!isDragging); //ugly hack to rerender the backlogs 
            setIsDragging(isDragging);
        });

        graph.current.addListener(mx.mxEvent.CELLS_ADDED, (sender: any, evt: any) => {
            copyCellLinks(evt.properties.cells);
            addCellsToState(glyphStateRef.current, evt.properties.cells);
        });

        model.addListener(mx.mxEvent.CHANGE, function (sender: any, evt: any) {
            var changes = evt.getProperty('edit').changes;
            onCellChange(changes);
        });

        graph.current.onGroupingCanceled = () => {
            UserControlService.showInfoPopup(
                {
                    title: `Action Disabled`,
                    content: `Grouping and ungrouping is only enabled in default states.`,
                    duration: 5000,
                    color: "danger"
                });
        }

        // prevents cells from being deleted when they are deleted within a non-default state and hides them instead
        graph.current.beforeCellRemoval = (cells: mxCell[]) => {
            if (glyphStateRef.current.id === "default") {
                // return true with this function to cancel cell removal in mxGraph;
                return false;
            }
            const states = glyphRef.current.states;
            for (const cell of cells) {
                hideCellInState(cell, glyphStateRef.current);
                let changeEntry = glyphStateRef.current.changes.find(c => c.cellId === cell.id);
                if (changeEntry) {
                    changeEntry.geometry = undefined;
                    changeEntry.style = { hide: "1" };
                    changeEntry.value = undefined;
                }
                if (MXGraphService.cellIsVisibleInNoState(cell.id, states)) {
                    graph.current.removeCells([cell]);
                }
            }
            editor.current.undoManager.clear();
            renderGlyphStates(activeGlyphStatesRef.current);
            return true;
        }

        graph.current.onRemoveLinkFromCell = (cell: mxCell, entry: IdeaBoxEntry) => {
            removeEntryLink(cell, entry);
        };

        // if a ideaboxentry label is dragged and dropped within the editor reposition it based on its target cell
        graph.current.handleLabelDrop = (event: any, origin: mxCell) => {
            const x = event.clientX + (svg.current.scrollLeft - svg.current.offsetLeft);
            const y = event.clientY + (svg.current.scrollTop - svg.current.offsetTop);
            const cell = graph.current.getCellAt(x, y);
            if (!cell) return;
            const entries = project.getIdeaboxEntriesOfGlyph(glyph);
            const entry = entries.find((e: IdeaBoxEntry) => e.id === event.entry.id);
            if (!entry) return;
            graph.current.model.beginUpdate();
            try {
                removeEntryLink(origin, event.entry);
                MXGraphService.linkCellToIdeaBoxEntry(cell, entry);
            } finally {
                graph.current.model.endUpdate();
            }
            refreshGraph();
            graph.current.setSelectionCells([cell]);
        }

        // load the used color history
        const colorDialog = frame.contentWindow.ColorDialog;
        colorDialog.recentColors = project.editorColors ? [...project.editorColors] : []
        // save the color history when colors are added to the color palette
        colorDialog.onRecentColorAdded = (colors: string[]) => {
            project.editorColors = [...colors];
        }

        if (props.compositionMode || props.presentationMode) {
            renderGlobalState(project.globalStates[0])
            return
        }
        renderGlyphStates([]);
    }

    const refreshGraph = () => {
        for (const glyph of project.glyphs) {
            let cell = glyph.model;
            if (props.compositionMode || props.presentationMode) {
                cell.setVisible(true);
                MXGraphService.enableCell(cell);
            }
            else {
                cell.setVisible(false);
            }
        }

        glyphRef.current && glyphRef.current.model && glyphRef.current.model.setVisible(true);
        graph.current && graph.current.refresh && graph.current.refresh();
    }

    const loadEditor = (initialGlyph: Glyph) => {
        const frame: any = iframe.current;
        glyphRef.current = initialGlyph;
        if (!frame || !frame.contentWindow.editor || !frame.contentWindow.graph) {
            return;
        }

        // Clear renderEntries to prevent stale state visualizations
        renderEntries.length = 0;

        // Clear active glyph states to ensure clean slate
        setActiveGlyphStates([]);
        activeGlyphStatesRef.current = [];

        graph.current = frame.contentWindow.graph;
        MXGraphService.addIdeaBoxEntryLabelSupport(graph.current, frame.contentWindow, project.ideaBox);

        editor.current = frame.contentWindow.editor;
        svg.current = frame.contentWindow.document.getElementsByClassName("geDiagramContainer")[0];

        // Load from different properties based on mode
        let xmlToLoad = DEFAULT_XML;
        if (props.presentationMode) {
            // On first load of presentation mode, initialize with a copy of graphXML
            if (!project.presentationGraphXML && project.graphXML) {
                project.presentationGraphXML = project.graphXML;
            }
            xmlToLoad = project.presentationGraphXML ? project.presentationGraphXML : (project.graphXML ? project.graphXML : DEFAULT_XML);
        } else if (project.graphXML) {
            xmlToLoad = project.graphXML;
        }
        editor.current.loadXML(xmlToLoad);

        // Immediately apply default state styles to all cells after loading XML
        // This ensures loaded XML with stale state visualizations is corrected
        const defaultState = project.getDefaultGlyphState();
        graph.current.getModel().beginUpdate();
        try {
            // Collect all cell IDs from all states to ensure we process everything
            const cellIdsToProcess = new Set<string>();
            for (const change of defaultState.changes) {
                cellIdsToProcess.add(change.cellId);
            }
            // Also include cells from all glyph states
            if (initialGlyph && initialGlyph.states) {
                for (const state of initialGlyph.states) {
                    for (const change of state.changes) {
                        cellIdsToProcess.add(change.cellId);
                    }
                }
            }

            // Apply default styles to all relevant cells
            for (const cellId of cellIdsToProcess) {
                const cell = graph.current.getModel().getCell(cellId);
                if (cell) {
                    cell.setStyle(MXGraphService.styleObjectToString(MXGraphService.getCellStyleForGlyphStates(cell, defaultState, [])));
                    cell.setGeometry(MXGraphService.getCellGeometryForGlyphStates(cell, defaultState, []));
                    MXGraphService.setVariableCellLabel(cell, MXGraphService.getCellLabelForGlyphStates(cell, defaultState, []));
                    MXGraphService.setCellTerminals(cell, defaultState, [], graph.current.getModel());
                }
            }
        } finally {
            graph.current.getModel().endUpdate();
        }

        //remove any cells that are not included in the default state
        const allCells = MXGraphService.getAllChildrenOfCell(graph.current.model.root);
        allCells.shift();
        for (const cell of allCells) {
            if (!defaultState.changes.find(change => change.cellId === cell.id) && !project.glyphs.find(g => g.id === cell.id)) {
                graph.current.removeCells([cell]);
            }
        }

        for (const g of project.glyphs) {
            let cell = graph.current.model.getCell(g.id)
            if (!cell) cell = graph.current.addCell(new mx.mxCell(g.name), graph.current.model.root);
            cell.setId(g.id);
            g.model = cell;
            if (cell.id !== initialGlyph.id) {
                MXGraphService.disableCell(cell);
            } else {
                MXGraphService.enableCell(cell);
            }
        }

        fixOldGlyphs();

        // Set both state and ref to default state
        const defaultGlyphState = glyphRef.current.getDefaultState();
        setGlyphState(defaultGlyphState);
        glyphStateRef.current = defaultGlyphState;
        graph.current.orderCells(false, [initialGlyph.model]);
        graph.current.setDefaultParent(initialGlyph.model);

        refreshGraph();

        // Render the default state (no active states) after graph is fully initialized
        // Use setTimeout to ensure this runs after the graph rendering is complete
        setTimeout(() => {
            renderGlyphStates([]);
        }, 0);
    }

    // currently removes all links instead of copying them
    const copyCellLinks = (cells: mxCell[]) => {
        //const glyphEntries = Glyph.getAllGlyphEntries(glyphRef.current);
        for (const cell of cells) {
            cell.setAttribute('entryIDs', "[]");
            cell.setAttribute('entry-labels', "");
            cell.children && copyCellLinks(cell.children);
            /** USE THIS INSTEAD OF THE PREVIOUS TTHREE LINES IF YOU WANT TO ALSO COPY LINKS FOR COPIED CELLS INSTEAD OF REMOVING THEM
            const entryIDs = JSON.parse(cell.getAttribute("entryIDs", "[]")) || [];
            for (const id of entryIDs) {
                const entry = glyphEntries.find((e: IdeaBoxEntry) => e.id === id);
                if (entry) {
                    entry.linkedCells.push(cell.id);
                }
            }
                 */
        }
    }

    const removeCellLinks = (cells: mxCell[]) => {
        for (const entry of project.ideaBox.entries) {
            for (const cell of cells) {
                MXGraphService.unlinkCellFromIdeaboxEntry(cell, entry);
                if (cell.children && cell.children.length > 0) {
                    removeCellLinks(cell.children);
                }
            }
        }
    }

    const onDragStart = (result: any) => {
        setIsDragging(result.draggableId);
    }

    const onDragEnd = (e: any) => {
        setIsDragging(false);
        graph.current.highlightCellsWithEntry([], true);
        const entries = project.ideaBox.entries;
        const entry = entries.find((e: IdeaBoxEntry) => e.id === isDragging);
        if (!entry) return
        const selection = graph.current.getSelectionCells();
        // Add cell at mouse position to selection if one or no cell selected
        if (selection.length <= 1) {
            const cell = getCellAtMouseEvent(e);
            cell && (selection[0] = cell);
        }
        selection.length > 0 && setCellAnimationAnchor(selection[0], e);
        graph.current.model.beginUpdate();
        try {
            selection.forEach((cell: any) => {
                MXGraphService.linkCellToIdeaBoxEntry(cell, entry);
            });
        } finally {
            graph.current.model.endUpdate();
        }
        refreshGraph();
        graph.current.setSelectionCells(selection);
    }

    const getCellAtMouseEvent = (e: any) => {
        const bounds = e.target.getBoundingClientRect();
        const pos = { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
        pos.x += svg.current.scrollLeft - svg.current.offsetLeft;
        pos.y += svg.current.scrollTop - svg.current.offsetTop;
        return graph.current.getCellAt(pos.x, pos.y);
    }

    const setCellAnimationAnchor = (cell?: mxCell, e?: MouseEvent) => {
        if (!cell || !e) {
            animationAnchor.current = { x: 0, y: 0 }
            return false;
        }
        const state = graph.current.view.getState(cell);
        animationAnchor.current.x = state.x + state.width / 2 - svg.current.scrollLeft + svg.current.offsetLeft - iframe.current.offsetLeft;
        animationAnchor.current.y = state.y + state.height / 2 - svg.current.scrollTop + svg.current.offsetTop - iframe.current.offsetTop;
        return animationAnchor.current;
    }

    const backlogHovered = (entry: IdeaBoxEntry, isHovered: boolean) => {
        loaded && graph.current.highlightCellsWithEntry(entry, isHovered);
    }

    const removeEntryLink = (cell: mxCell, entry: IdeaBoxEntry) => {
        MXGraphService.unlinkCellFromIdeaboxEntry(cell, entry);
        graph.current.setSelectionCells([]);
        setIsDragging(!isDragging); //ugly hack to rerender the backlogs
        setIsDragging(isDragging);
    }

    const getDragStyle = (isDragging: boolean | string, isDraggingOver: boolean) => {
        const style: any = { pointerEvents: "all", filter: "", zIndex: "1" }
        if (isDragging) {
            style.zIndex = "-1";
            style.pointerEvents = "none";
            svg.current && (svg.current.childNodes[0].style.filter = "brightness(0.9)");
        } else {
            svg.current && (svg.current.childNodes[0].style.filter = "");
        }
        if (isDraggingOver) {
            //style.filter = "brightness(0.9)";
        }
        return style;
    }

    const renderGlyphMenuBar = useMemo(() => {
        if (props.compositionMode) {
            return <></>
        }
        return <div className='glyph-menu-bar mb-3' style={{ whiteSpace: "nowrap", overflowX: "auto", marginBottom: "1rem" }}>
            <span style={{ borderColor: "transparent" }}>
                Glyphs:
            </span>
            {project.glyphs.map((gly: Glyph, index) =>
                <span key={'gmb' + index}
                    className={glyph.id === gly.id ? "active" : ""}
                    onClick={() => { editor.current.undoManager.clear(); setGlyph(gly); }}>
                    {gly.name ? gly.name : "Unnamed Glyph"}
                </span>
            )}
            {/**
            <div className="sidenav-setting d-flex justify-content-between align-center ms-3" style={{ alignItems: "center" }}>
                <MDBSwitch onChange={(e: any) => { toggleGlyphVisiblity(e); }} defaultChecked />
                <span style={{ fontWeight: "bold" }} className='text-dark'>Hide Glyphs</span>
            </div>
              */}
            <span className={"download-button"}
                onClick={() => { setShowInfo(InfoWindowState.GLYPH_SKETCHING) }}>
                <i className="fas fa-question" style={{ margin: "0 -1px" }}></i>
            </span>
        </div>
    }, [project, glyph, props.compositionMode, glyphState]);  // eslint-disable-line react-hooks/exhaustive-deps

    ///// Functions for managing glyph states
    const addCellsToState = (state: GlyphState, cells: mxCell[]) => {
        const defaultState = glyphRef.current.getDefaultState();

        for (const cell of cells) {
            let changeEntry = new GlyphChange(cell.id, annotationModeRef.current);
            changeEntry.style = MXGraphService.styleStringToObject(cell.style);
            changeEntry.geometry = { ...cell.getGeometry() };
            const value = cell.getValue();
            if (value) {
                changeEntry.value = value.getAttribute ? value.getAttribute("label") : value;
            }

            //either add the new change or replace it if it already exists
            let index = defaultState.changes.findIndex(c => c.cellId === cell.id);
            if (index >= 0) {
                defaultState.changes.splice(index, 1, changeEntry);
            } else {
                defaultState.changes.push(changeEntry)
            }

            // hide the cell in the default state if added to a state that is not the default state
            if (state.id !== "default") {
                hideCellInState(cell, defaultState);
            }

            // also add children to the state if there are any
            cell.children && cell.children.length > 0 && addCellsToState(defaultState, cell.children);
        }
    }

    const removeCellsFromState = (state: GlyphState, cells: mxCell[]) => {
        for (const g of project.glyphs) {
            for (const state of g.states) {
                state.changes = state.changes.filter(c => !cells.find(cell => cell && cell.id === c.cellId));
            }
        }
    }

    const onCellChange = (changes: any[]) => {
        for (const change of changes) {
            if (change.parent || change.child) {
                //handle parent changes
                //return;
            }
            if (!change.cell) continue
            let changeEntry = glyphStateRef.current.changes.find(c => c.cellId === change.cell.id);
            if (!changeEntry) {
                changeEntry = new GlyphChange(change.cell.id, annotationModeRef.current);
                glyphStateRef.current.changes.push(changeEntry);
            }
            if (change.style) {
                changeEntry.style = Object.assign(changeEntry.style, MXGraphService.getStyleDifference(change.previous, change.style));
            }
            if (change.geometry) {
                let defaultEntry = glyphRef.current.getDefaultState().changes.find(c => c.cellId === change.cell.id);
                if (glyphStateRef.current.id === "default") { //default state always holds the full geometry
                    changeEntry.geometry = Object.assign(changeEntry.geometry, change.geometry);
                } else if (defaultEntry && defaultEntry.geometry) { //every other state holds the difference that has to be added to the default geometry while rendering
                    changeEntry.geometry = Object.assign(changeEntry.geometry, MXGraphService.getGeometryDifference(defaultEntry.geometry, change.geometry));
                }
            }
            if ((change.value || change.value === "")) {
                //if value is a node, get the label attribute, otherwise just the plain value
                changeEntry.value = change.value.getAttribute ? change.value.getAttribute("label") : change.value;
            }
            changeEntry.target = change.cell.target ? change.cell.target.id : undefined;
            changeEntry.source = change.cell.source ? change.cell.source.id : undefined;

        }
    }

    const renderGlyphStates = (states: GlyphState[]) => {
        if (!graph.current) {
            return;
        }
        const baseState = project.getDefaultGlyphState();
        graph.current.getModel().beginUpdate();
        try {
            // Collect all unique cell IDs that have changes in base state or any state
            const cellIdsToProcess = new Set<string>();
            for (const change of baseState.changes) {
                cellIdsToProcess.add(change.cellId);
            }
            for (const state of states) {
                for (const change of state.changes) {
                    cellIdsToProcess.add(change.cellId);
                }
            }

            // When rendering default state (empty states array), also include cells from ALL glyph states
            // to ensure cells with state-specific changes are reverted to default
            if (states.length === 0) {
                // In composition/presentation mode, check ALL glyphs' states, not just current glyph
                const glyphsToCheck = (props.compositionMode || props.presentationMode) ? project.glyphs : [glyphRef.current];

                for (const glyph of glyphsToCheck) {
                    if (!glyph || !glyph.states) continue;
                    for (const glyphState of glyph.states) {
                        for (const change of glyphState.changes) {
                            cellIdsToProcess.add(change.cellId);
                        }
                    }
                }
            }

            // Process all cells that have changes in any relevant state
            for (const cellId of cellIdsToProcess) {
                const cell = graph.current.getModel().getCell(cellId);
                if (!cell) {
                    continue;
                }

                // Check if this cell has a change in baseState
                const baseStateChange = baseState.changes.find(c => c.cellId === cellId);

                // If rendering default state (states.length === 0) and cell has no baseState change,
                // we need to ensure state-specific styling is removed
                if (states.length === 0 && !baseStateChange) {
                    // For cells not in baseState, look for the first state that has this cell
                    // to get its base styling from that state's perspective
                    let foundInState = false;
                    if (glyphRef.current) {
                        for (const glyphState of glyphRef.current.states) {
                            const stateChange = glyphState.changes.find(c => c.cellId === cellId);
                            if (stateChange && glyphState.id !== 'default') {
                                // This cell exists in a non-default state, so render it with empty state
                                // by using baseState with this cell's change added temporarily
                                const tempBaseState = { ...baseState };
                                tempBaseState.changes = [...baseState.changes];
                                // Don't add the change, just use empty style to clear state styling
                                foundInState = true;
                                break;
                            }
                        }
                    }
                }

                cell.setStyle(MXGraphService.styleObjectToString(MXGraphService.getCellStyleForGlyphStates(cell, baseState, states)));
                cell.setGeometry(MXGraphService.getCellGeometryForGlyphStates(cell, baseState, states));
                MXGraphService.setVariableCellLabel(cell, MXGraphService.getCellLabelForGlyphStates(cell, baseState, states));
                MXGraphService.setCellTerminals(cell, baseState, states, graph.current.getModel());
            }
        }
        finally {
            graph.current.getModel().endUpdate();
            refreshGraph();
            graph.current.setSelectionCells([]);
        }
    }

    const addGlyphState = () => {
        const newState = new GlyphState(nanoid(), "");
        glyphRef.current.states.push(newState);
        editGlyphState(newState);
    }

    const editGlyphState = (state: GlyphState, isDefault?: boolean) => {
        graph.current.isNotInDefaultState = !isDefault;
        editor.current.undoManager.clear();
        glyphStateRef.current = state;
        setActiveGlyphStates(isDefault ? [] : [state]);
        setGlyphState(state);
        renderGlyphStates(isDefault ? [] : [state]);
    }

    const toggleActiveState = (state: GlyphState) => {
        const i = activeGlyphStates.findIndex(s => s.id === state.id);
        if (i >= 0 && state.id === glyphStateRef.current.id) {
            setGlyphState(glyphRef.current.getDefaultState())
        }
        if (i >= 0) {
            activeGlyphStates.splice(i, 1);
        } else {
            activeGlyphStates.push(state);
        }
        setActiveGlyphStates([...activeGlyphStates]);
        renderGlyphStates([...activeGlyphStates])
    }

    const editDefaultState = () => {
        editGlyphState(glyphRef.current.getDefaultState(), true);
    }

    const copyGlyphState = (state: GlyphState) => {
        const copy = cloneDeep(state);
        copy.id = nanoid();
        copy.name = state.name + "_copy";
        glyphRef.current.states.push(copy);
        editGlyphState(copy);
    }

    const deleteGlyphState = (state: GlyphState) => {
        const i = glyphRef.current.states.findIndex(s => s.id === state.id);
        if (i < 0) return
        glyphRef.current.states.splice(i, 1);
        const states = glyphRef.current.states;
        for (const change of state.changes) {
            if (MXGraphService.cellIsVisibleInNoState(change.cellId, states)) {
                const cell = graph.current.getModel().getCell(change.cellId);
                graph.current.removeCells([cell]);
            }
        }
        editDefaultState();
    }

    const hideCellInState = (cell: mxCell, state: GlyphState, dontHide?: boolean) => {
        let changeEntry = state.changes.find(c => c.cellId === cell.id);
        if (!changeEntry) {
            changeEntry = new GlyphChange(cell.id, annotationModeRef.current);
            changeEntry.style.hide = dontHide ? "0" : "1";
            state.changes.push(changeEntry);
            return;
        }
        if (!changeEntry.style) {
            changeEntry.style = {};
        }
        changeEntry.style.hide = dontHide ? "0" : "1";
    }

    const renderStateMenuBar = useMemo(() => {
        if (props.compositionMode || !glyph.states) {
            return <></>
        }
        return <div className='glyph-menu-bar mb-3'>
            <span style={{ borderColor: "transparent" }}>
                View States:
            </span>
            {glyph.states.map((sta: GlyphState, index) =>
                <GlyphEditorState
                    key={index}
                    state={sta}
                    isEdit={glyphState.id === sta.id}
                    isDefaultState={sta.id === "default"}
                    isActive={!!activeGlyphStates.find(s => s.id === sta.id)}
                    onCopy={copyGlyphState}
                    onDefaultEdit={editDefaultState}
                    onDelete={deleteGlyphState}
                    onEdit={editGlyphState}
                    onMouseEnter={() => { index > 0 ? renderGlyphStates(glyphState.id === sta.id ? [...activeGlyphStates] : [...activeGlyphStates, sta]) : renderGlyphStates([]) }}
                    onMouseLeave={() => { renderGlyphStates([...activeGlyphStates]) }}
                    onToggleActive={() => { toggleActiveState(sta) }}
                />
            )}
            <span className={"active"}
                onClick={() => { addGlyphState() }}>
                +
            </span>
        </div>
    }, [project, glyph, props.compositionMode, glyphState, activeGlyphStates]);  // eslint-disable-line react-hooks/exhaustive-deps

    ////////////////////////////////////
    // Functions for managing global states

    const addGlobalState = () => {
        const newState = new GlobalState(nanoid(), "");
        project.globalStates.push(newState)
        setGlobalState(newState);
        renderGlobalState(newState);
    }

    const editGlobalState = (state: GlobalState) => {
        const actualState = state ? state : project.globalStates[0];
        setGlobalState(actualState);
        renderGlobalState(actualState);
    }

    const deleteGlobalState = (state: GlobalState) => {
        const i = project.globalStates.findIndex(s => s.id === state.id);
        if (i < 0) return
        project.globalStates.splice(i, 1);
        setGlobalState(project.globalStates[0]);
        renderGlobalState(project.globalStates[0]);
    }

    const renderGlobalState = (globalState: GlobalState, add?: GlyphState) => {
        if (!graph.current) {
            return;
        }
        let states = project.getGlyphStatesOfGlobalState(globalState);
        if (add) {
            states.push(add)
        }

        renderGlyphStates(states);
    }

    const toggleGlobalState = (globalState: GlobalState, state: GlyphState) => {
        const i = globalState.activeGlyphStateIds.findIndex(id => id === state.id);
        if (i < 0) {
            globalState.activeGlyphStateIds.push(state.id)
        } else {
            globalState.activeGlyphStateIds.splice(i, 1);
        }
        setGlobalState({ ...globalState });
    }

    const renderGlobalStateBar = useMemo(() => {
        if (!project.globalStates) {
            return <></>
        }
        let allStates: GlyphState[] = [];
        for (const glyph of project.glyphs) {
            allStates = [...allStates, ...glyph.states.filter(s => s.id !== "default")]
        }

        return <><div className='glyph-menu-bar global-states-bar-top' style={{ whiteSpace: "nowrap", overflowX: "auto", marginBottom: "1rem" }}>
            <span style={{ borderColor: "transparent" }}>
                Global States:
            </span>
            {project.globalStates.map((sta: GlobalState, index) =>
                <GlyphEditorState
                    key={index}
                    state={sta}
                    isEdit={globalState.id === sta.id}
                    isDefaultState={index <= 0}
                    isActive={false}
                    onDefaultEdit={editGlobalState}
                    onDelete={deleteGlobalState}
                    onEdit={editGlobalState}
                    onMouseEnter={() => { renderGlobalState(sta) }}
                    onMouseLeave={() => { renderGlobalState(globalState) }}
                />
            )}
            <span className={"active"}
                onClick={() => { addGlobalState() }}>
                +
            </span>
            {!props.compositionMode && (
                <div className={"download-buttons-container"}>
                    <span className={"download-button"}
                        onClick={() => { MXGraphService.exportGraph(graph.current) }}>
                        State PNG <i className="fas fa-download" />
                    </span>
                    <span className={"download-button"}
                        onClick={async () => {
                            setIsExporting(true);
                            setExportProgress('Preparing export...');
                            try {
                                await MXGraphService.exportAllGlobalStatesAsZip(
                                    graph.current,
                                    project,
                                    renderGlobalState,
                                    globalState,
                                    (progress: string) => setExportProgress(progress)
                                );
                            } finally {
                                setIsExporting(false);
                            }
                        }}>
                        All State PNGs <i className="fas fa-download" />
                    </span>
                </div>
            )}
        </div>
            <div style={{ whiteSpace: "nowrap", overflowX: "auto", marginBottom: "1rem" }}>
                {project.glyphs.map((g, index) => g.states.length > 0 ? <div key={"t" + index} className='glyph-menu-bar global-states-bar pt-1'>{g.states.map((sta: GlyphState, index) => {
                    if (sta.id === "default") return undefined
                    return <GlyphEditorState
                        key={index}
                        state={sta}
                        isEdit={false}
                        onEdit={() => { toggleGlobalState(globalState, sta) }}
                        isActive={!!globalState.activeGlyphStateIds.find(id => id === sta.id)}
                        onMouseEnter={() => { renderGlobalState(globalState, sta) }}
                        onMouseLeave={() => { renderGlobalState(globalState) }}
                        onToggleActive={() => { toggleGlobalState(globalState, sta) }}
                    />
                }
                )}<div className='state-title'>{g.name}</div></div> : null)}
            </div>
        </>
    }, [project, glyph, props.compositionMode, props.presentationMode, glyphState, activeGlyphStates, globalState]);  // eslint-disable-line react-hooks/exhaustive-deps


    ////////////////////////////////////
    // Updates old projects to support the state system
    const fixOldGlyphs = () => {
        //@ts-ignore Support glyphs that had no states prior
        if (!project.defaultState) {
            for (const glyph of project.glyphs) {
                if (glyph.states || !glyph.model) {
                    continue;
                }
                glyph.states = [new GlyphState("default", "default")];
                const cells = MXGraphService.getAllChildrenOfCell(glyph.model);
                MXGraphService.enableCell(glyph.model);
                if (cells.length > 1) {
                    cells.shift(); //remove first child cell because that is the glyph container
                    //@ts-ignore
                    addCellsToState(glyph.getDefaultState(), cells);
                }
            }
            return
        }

        //@ts-ignore // Support glyphs that were dependend on the globa default state in project.defaultState
        if (!project.defaultState || project.defaultState.changes.length <= 0) return
        for (const glyph of project.glyphs) {
            if (!glyph.model) continue
            if (!glyph.states) {
                glyph.states = [new GlyphState("default", "default")];
            }
            if (!glyph.getDefaultState()) {
                glyph.states.unshift(new GlyphState("default", "default"));
            }

            const cells = MXGraphService.getAllChildrenOfCell(glyph.model);
            MXGraphService.enableCell(glyph.model);
            const defaultState = glyph.getDefaultState();
            for (const cell of cells) {
                //@ts-ignore
                const change = project.defaultState.changes.find(c => c.cellId === cell.id);
                if (!change) continue
                const index = defaultState.changes.findIndex(c => c.cellId === change.id)
                if (index >= 0) {
                    defaultState.changes[index] = change;
                } else {
                    defaultState.changes.push(change);
                }
            }
        }

        //@ts-ignore
        delete project["defaultState"];
    }
    /////////////////////////////////

    const entries = project.getIdeaboxEntriesOfGlyph(glyph);

    return <div id="glyph-editor">
        {!props.compositionMode && !props.presentationMode && renderGlyphMenuBar}
        {!props.compositionMode && !props.presentationMode ? renderStateMenuBar : renderGlobalStateBar}
        <DragDropContext onDragEnd={() => { setIsDragging(false) }} onBeforeCapture={onDragStart}>
            <div className='d-flex flex-grow-1' style={{ height: "0%" }}>
                <Droppable droppableId="glyph-editor" direction="vertical" type={DropType.TASK}>
                    {(provided, snapshot) => (
                        <div {...provided.droppableProps}
                            ref={provided.innerRef}
                            className='glyph-editor-frame-container' style={{ flex: 1, ...(isDragging ? { cursor: "crosshair" } : {}) }}>
                            {!loaded && <i className={"fas fa-spinner glyph-editor-spinner"} />}
                            <div onMouseLeave={() => { animationAnchor.current = { x: 0, y: 0 }; }}
                                onMouseUp={onDragEnd}
                            >
                                <iframe id={"glyph-editor-frame"}
                                    className='glyph-editor-frame' title="glyph-editor-frame"
                                    ref={iframe}
                                    style={getDragStyle(isDragging, snapshot.isDraggingOver)}
                                    src={EDITOR_URL} />
                                {props.presentationMode &&
                                    <div className="annotate-button sidenav-setting d-flex justify-content-between align-center ms-3" style={{ alignItems: "center", backgroundColor: "#f93154", padding: "0.5rem 1rem" }}>
                                        <span style={{ fontWeight: "bold" }} className='text-white'>Annotation Mode</span>
                                        <MDBTooltip tag='span' wrapperClass='d-inline-block' title='Use annotation mode if you want to add elements or descriptions to your design that are not an actual part of the design. All elements that are added to your design while the annotation mode is turned on will be ignored during the KONECT-Value calculation.'>
                                            <i className="fas fa-question card-exclamation"></i></MDBTooltip>
                                    </div>}
                            </div>
                            <div style={{ display: "none" }}>{provided.placeholder}</div>
                        </div>
                    )}
                </Droppable>

                {(!props.compositionMode && !props.presentationMode) && (
                    <div className='backlog-container'>
                        <Backlog entries={entries}
                            type={TaskType.INFORMATION}
                            name="Perception"
                            animateToRef={animationAnchor}
                            onHover={backlogHovered} />
                        <Backlog entries={entries}
                            type={TaskType.INTERACTION}
                            name="Interaction"
                            animateToRef={animationAnchor}
                            onHover={backlogHovered} />
                    </div>
                )}

            </div>
        </DragDropContext>

        {isExporting && (
            <div className="export-progress-overlay">
                <div className="export-progress-dialog">
                    <div className="export-progress-spinner">
                        <i className="fas fa-spinner fa-spin fa-3x"></i>
                    </div>
                    <h3>Please wait...</h3>
                    <p>{exportProgress}</p>
                </div>
            </div>
        )}

    </div>
}